import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, inject } from '@angular/core';
import { TEMPLATE_CONSTANTS_IDS } from '@constants/app.constants';
import { CssOverwritesDbModel } from '@data-models/css-overwrites.model';
import { FeaturesDbModel } from '@data-models/features-db.model';
import { IPartnerInformationBody, PartnerDbModel, TokenType } from '@data-models/partner-db.model';
import { PartnerSettingDbModel } from '@data-models/partner-setting-db.model';
import { CustomBookingMessageTemplateDbModel } from '@data-models/templates-db.model';
import { environment } from '@env/environment';
import { DomManipulationService } from '@services/util-service/dom-manipulation.service';
import { HttpClientService } from '@services/util-service/http-client.service';
import { LoggerService } from '@services/util-service/logger.service';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  partnerLoadEvent = new EventEmitter<PartnerDbModel>();
  partnerCssOverwritesEvent = new EventEmitter<CssOverwritesDbModel>();
  readonly templateConstantsIds = TEMPLATE_CONSTANTS_IDS;

  private httpClientService = inject(HttpClientService);
  private domManipulationService = inject(DomManipulationService);
  protected featuresList: { [key: string]: boolean } = {};

  private _accessToken!: string;
  get accessToken(): string {
    return this._accessToken
  };

  private _partner!: PartnerDbModel;
  get partner(): PartnerDbModel {
    return this._partner;
  }

  private _isWhitelabeledPartner = false;
  get isWhitelabeledPartner(): boolean {
    return this._isWhitelabeledPartner;
  }

  private _partnerTemplates: CustomBookingMessageTemplateDbModel[] = [];
  get partnerTemplates(): CustomBookingMessageTemplateDbModel[] {
    return this._partnerTemplates;
  }

  private _partnerBookingName = environment.defaultBookingPartnerName;
  get partnerBookingName(): string {
    return this._partnerBookingName;
  }

  private _isInternal = false;
  get isInternal(): boolean {
    return this._isInternal;
  }

  restoreFromCache(partner: PartnerDbModel, isInternal = false): void {
    this._partner = partner;
    this._partner.has_active_subscription = true;
    this._accessToken = partner?.token;
    this._partnerBookingName = partner?.booking_name || this._partnerBookingName;
    this._isInternal = isInternal;
    this.loadSubscriptionFeatures(partner.features);
    this._isWhitelabeledPartner = this.hasFeature('is_whitelabel_enabled');
  }

  getPartnersWidgetInformation(
    body: IPartnerInformationBody,
    triggerpartnerLoadEvent = true
  ): Observable<PartnerDbModel> {
    this._partnerBookingName = body.booking_name;
    // Send the booking-page-specific token_type so the backend serializer
    // keeps partner contact PII (email/phone/address) in the response.
    // Widget surfaces (public_widget / internal_widget) never receive PII;
    // only the booking-page aliases unlock it (subject to per-partner
    // hide_* flags).
    body.token_type = TokenType.bookingPage;

    if (body?.internal && body?.token) {
      delete body.internal;
      body.token_type = TokenType.bookingPageInternal;
      body.token = body.token;
      this._isInternal = true;
    }

    return this.httpClientService.post('partners/information', body, {}).pipe(
      // Retry ONLY genuinely transient failures. A 403/404 is an authoritative answer
      // (blocked / booking disabled / no such booking name) — retrying it just doubles
      // the wait before the same verdict. Under concurrent load this call was failing
      // for ~1 render in 8 on DEV while the API itself answered 24/24 OK, so the loss is
      // in the SSR process, not the backend; one cheap retry recovers most of it. What
      // still fails falls through to the 503 in PartnerResolveFn.
      retry({
        count: 1,
        delay: (error: HttpErrorResponse) =>
          (error.status === 0 || error.status >= 500) ? timer(400) : throwError(() => error)
      }),
      switchMap((partner: PartnerDbModel) => {
        partner.country.is_multi_language = 1;
        this._partner = partner;
        this._partner.has_active_subscription = true;
        this._accessToken = this.partner?.token;
        this.loadSubscriptionFeatures(partner.features);
        this._isWhitelabeledPartner = this.hasFeature('is_whitelabel_enabled');
        if (triggerpartnerLoadEvent) {
          this.partnerLoadEvent.emit(this.partner);
        }
        return of(this.partner);
      }),
      catchError((error: HttpErrorResponse) => {
        LoggerService.error(error);
        throw error;
      })
    );
  }

  getPartnerTemplates(): Observable<CustomBookingMessageTemplateDbModel[]> {
    return this.httpClientService.post('templates/templates_by_ids', {
      ids: [
        this.templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_PAGETITLE,
        this.templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_APPOINTMENT,
        this.templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_EVENT,
        this.templateConstantsIds.EVENT_WIDGET_LABELS.WIDGET_EVENT_BOOK_LABEL,
        this.templateConstantsIds.EVENT_WIDGET_LABELS.WIDGET_EVENT_NO_MORE_FREE_SLOTS_LABEL
      ]
    }, {}).pipe(
      // One cheap retry before giving up. This call decides the page's <title> and
      // meta description, and losing it to a single transient blip was measurably
      // common under crawler-level concurrency (~1 render in 12 on production).
      // The resolver's fallback covers what still fails; this just makes that rarer.
      retry({ count: 1, delay: 400 }),
      catchError(() => {
        LoggerService.warn('Error while getting partner template');
        this._partnerTemplates = [];
        return of([]);
      }),
      switchMap((templates: CustomBookingMessageTemplateDbModel[]) => {
        this._partnerTemplates = templates;
        return of(this.partnerTemplates);
      })
    );
  }

  loadWidgetAndConfigure(): void {
    this.domManipulationService.appendWidgetWebComponentDependencies();
  }

  getPartnerSettingsByIds(ids: number[]): Observable<PartnerSettingDbModel[]> {
    const body: { ids: number[] } = { ids };
    return this.httpClientService.post('partners_settings/getSettingsByIds', body, {}).pipe(
      catchError((error: HttpErrorResponse) => {
        LoggerService.error(error);
        return of([]);
      }),
      switchMap((result: PartnerSettingDbModel[]) => {
        if (result) {
          return of(result);
        } else {
          return of([]);
        }
      })
    );
  }

  protected loadSubscriptionFeatures(features: FeaturesDbModel[]): void {
    if (features?.length > 0) {
      features.forEach(feature => {
        this.featuresList[feature.feature.name] = true;
      });
    }
  }

  hasFeature(feature: string): boolean {
    return this.featuresList[feature] ? this.featuresList[feature] : false;
  }

  getCssOverwrites(): Observable<CssOverwritesDbModel> {
    return this.httpClientService.get(`css_overwrites/active?type=booking_page`, {}).pipe(
      catchError(() => {
        return of(null);
      })
    );
  }
}
